import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import ReactQuill from "react-quill";
import 'react-quill/dist/quill.snow.css';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import StandardButton from "../components/standardButton";

export default function InboxMessageEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const quillRef = useRef(null);

  const [templateData, setTemplateData] = useState(null);
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [text, setText] = useState("");
  const [timingId, setTimingId] = useState(1);
  const [languageId, setLanguageId] = useState(1);
  const [editingName, setEditingName] = useState(false);
  const [editingSubject, setEditingSubject] = useState(false);

  useEffect(() => {
    const fetchTemplate = async () => {
      if (!id) return;

      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/templates/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const data = res.data;
        setTemplateData(data);
        setName(data.name);
        setSubject(data.subject);
        setText(data.text);
        setTimingId(data.timing_id);
        setLanguageId(data.language_id);
      } catch (error) {
        console.error("Error fetching template:", error);
      }
    };

    fetchTemplate();
  }, [id]);

  const handleSubmit = async () => {
    const payload = {
      name,
      subject,
      text,
      language_id: languageId
    };

    try {
      const token = localStorage.getItem("token");
      if (templateData) {
        await axios.patch(`${process.env.REACT_APP_API_URL}/templates/${id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/templates`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      navigate("/inbox-messages");
    } catch (error) {
      console.error("Error saving template:", error);
    }
  };

  // Quill modules configuration
  const modules = {
    toolbar: {
      container: [
        [{ header: [1, 2, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        ['link'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['clean']
      ]
    }
  };

  const formats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'link', 'list', 'bullet'
  ];

  return (
    <div className="p-5 flex justify-center items-start h-full max-h-[100vh] overflow-y-auto">
      <div className="p-5 flex flex-col w-full h-fit bg-[#191919] rounded-2xl gap-6">
        {/* NOME TEMPLATE */}
        <div className="flex items-center text-3xl font-semibold text-white gap-2">
          {editingName ? (
            <>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="bg-[#2a2a2a] text-white rounded px-3 py-1 text-lg"
                onKeyDown={(e) => e.key === 'Enter' && setEditingName(false)}
                autoFocus
              />
              <CheckIcon className="cursor-pointer" sx={{ color: '#C5FF55' }} onClick={() => setEditingName(false)} />
            </>
          ) : (
            <>
              <span>{name || "Untitled Template"}</span>
              <EditIcon className="cursor-pointer" sx={{ color: '#C5FF55' }} onClick={() => setEditingName(true)} />
            </>
          )}
        </div>        

        {/* SUBJECT */}
        <div className="flex items-center text-xl text-white gap-2">
          {editingSubject ? (
            <>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="bg-[#2a2a2a] text-white rounded px-3 py-1 text-base w-full"
                onKeyDown={(e) => e.key === 'Enter' && setEditingSubject(false)}
                autoFocus
              />
              <CheckIcon className="cursor-pointer" sx={{ color: '#C5FF55' }} onClick={() => setEditingSubject(false)} />
            </>
          ) : (
            <>
              <span className="text-lg">{subject || "No subject"}</span>
              <EditIcon className="cursor-pointer" sx={{ color: '#C5FF55' }} onClick={() => setEditingSubject(true)} />
            </>
          )}
        </div>

        {/* TIMING NAME */}
        {templateData?.timing?.name && (
          <div className="text-white text-sm italic">
            Notification type: <span className="text-[#C5FF55] font-medium">{templateData.timing.name}</span>
          </div>
        )}

        {/* WYSIWYG EDITOR */}
        <div className="h-[300px] overflow-hidden bg-white rounded">
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={text}
            onChange={setText}
            modules={modules}
            formats={formats}
            style={{ height: '250px' }}
          />
        </div>

        {/* PARAMETERS */}
        {templateData?.timing?.parameters?.length > 0 && (
          <div className="text-white text-sm mt-4">
            <p className="mb-2">To include dynamic parameters in your message, use the format:</p>
            <ul className="list-disc list-inside">
              {templateData.timing.parameters.map((param, i) => (
                <li key={i}><code className="bg-[#2a2a2a] px-1 rounded">{`{{${param}}}`}</code></li>
              ))}
            </ul>
          </div>
        )}

        {/* BUTTON */}
        <div className="flex justify-end mt-6">
          <StandardButton
            text={templateData ? "Save" : "Create"}
            onClick={handleSubmit}
            width={"200px"}
          />
        </div>
      </div>
    </div>
  );
}